import { useEffect, useState } from 'react'
import { useStorePads } from '../store'

const targets = new Map()
const assignmentToTarget = new Map()
let midiAccess = null
let initialized = false
let learningTarget = null
let selectedInputId = ''
let midiInputs = []
let midiSupported = false
const subscribers = new Set()
const messageSubscribers = new Set()

const getNoteName = (note) => {
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
    if (note == null || Number.isNaN(note)) return ''
    const octave = Math.floor(note / 12) - 1
    return `${noteNames[note % 12] ?? '?'}${octave}`
}

const emitMidiState = () => {
    subscribers.forEach((cb) => cb({ midiInputs, selectedInputId, isSupported: midiSupported }))
}

const emitMidiMessage = (message) => {
    messageSubscribers.forEach((cb) => cb(message))
}

const handleMidiMessage = (event) => {
    const inputId =
        event.target?.id ||
        event.currentTarget?.id ||
        event.target?.name ||
        event.currentTarget?.name

    if (selectedInputId && inputId !== selectedInputId) return

    const [status, data1, data2] = event.data
    const command = status & 0xf0

    let assignmentKey = null
    let message = null
    let isNoteOn = false

    // NOTE ON
    if (command === 0x90 && data2 > 0) {
        assignmentKey = `note:${data1}`
        message = { type: 'note', number: data1, value: data2 }
        isNoteOn = true
    }
    // NOTE OFF
    else if (command === 0x80 || (command === 0x90 && data2 === 0)) {
        assignmentKey = `note:${data1}`
        message = { type: 'note', number: data1, value: data2 }
    }
    // CC (KNOBS / FADERS)
    else if (command === 0xb0) {
        assignmentKey = `cc:${data1}`
        message = { type: 'cc', number: data1, value: data2 }
    }

    if (!assignmentKey || !message) return

    emitMidiMessage({
        ...message,
        inputId
    })

    // ✅ FIX: permitir aprendizaje con CC también
    if (learningTarget && (message.type === 'cc' || isNoteOn)) {
        clearAssignmentForTarget(learningTarget)

        assignmentToTarget.set(assignmentKey, learningTarget)

        const target = targets.get(learningTarget)

        target?.onMidiAssigned?.(message)
        useStorePads.getState().setMidiAssignment(learningTarget, message)

        learningTarget = null

        target?.onMidiMessage?.(message)
        return
    }

    const targetId = assignmentToTarget.get(assignmentKey)
    if (!targetId) return

    // Ignorar note off
    if (message.type === 'note' && !isNoteOn) return

    const target = targets.get(targetId)
    target?.onMidiMessage?.(message)
}

const clearAssignmentForTarget = (targetId) => {
    for (const [assignmentKey, mappedTarget] of assignmentToTarget.entries()) {
        if (mappedTarget === targetId) {
            assignmentToTarget.delete(assignmentKey)
        }
    }
}

const attachInput = (input) => {
    if (!input) return
    input.onmidimessage = handleMidiMessage
}

const updateMidiInputs = () => {
    if (!midiAccess) return
    midiInputs = [...midiAccess.inputs.values()]
    emitMidiState()
}

const initMidi = async () => {
    if (initialized) return
    initialized = true

    midiSupported = typeof navigator !== 'undefined' && typeof navigator.requestMIDIAccess === 'function'
    if (!midiSupported) {
        emitMidiState()
        return
    }

    try {
        midiAccess = await navigator.requestMIDIAccess()

        updateMidiInputs()

        for (const input of midiAccess.inputs.values()) {
            attachInput(input)
        }

        midiAccess.onstatechange = () => {
            updateMidiInputs()
            for (const input of midiAccess.inputs.values()) {
                attachInput(input)
            }
        }
    } catch (_error) {
        // No MIDI support or permission denied
    }
}

const registerMidiTarget = (targetId, callbacks) => {
    if (!targetId) return
    targets.set(targetId, callbacks)
}

const unregisterMidiTarget = (targetId) => {
    targets.delete(targetId)

    if (learningTarget === targetId) learningTarget = null

    for (const [assignmentKey, mappedTarget] of assignmentToTarget.entries()) {
        if (mappedTarget === targetId) {
            assignmentToTarget.delete(assignmentKey)
        }
    }
}

const startMidiLearning = (targetId) => {
    if (!targetId) return
    learningTarget = targetId
}

const stopMidiLearning = () => {
    learningTarget = null
}

const getAssignedMidiAssignment = (targetId) => {
    if (!targetId) return null

    const storedAssignment = useStorePads.getState().midiAssignments[targetId]
    if (storedAssignment) return storedAssignment

    for (const [assignmentKey, mappedTarget] of assignmentToTarget.entries()) {
        if (mappedTarget === targetId) {
            const [type, number] = assignmentKey.split(':')
            return {
                type,
                number: Number(number)
            }
        }
    }

    return null
}

const selectMidiDevice = (deviceId) => {
    selectedInputId = deviceId
    useStorePads.getState().setSelectedMidiDeviceId(deviceId)
    emitMidiState()
}

const applyMidiAssignmentToTarget = (targetId, assignment) => {
    if (!targetId) return

    clearAssignmentForTarget(targetId)

    if (!assignment) return

    const assignmentKey =
        assignment.type === 'cc'
            ? `cc:${assignment.number}`
            : `note:${assignment.number}`

    assignmentToTarget.set(assignmentKey, targetId)
}

const subscribeMidiState = (callback) => {
    subscribers.add(callback)
    callback({ midiInputs, selectedInputId, isSupported: midiSupported })
    return () => subscribers.delete(callback)
}

const subscribeMidiMessages = (callback) => {
    messageSubscribers.add(callback)
    return () => messageSubscribers.delete(callback)
}

const useMidiMapping = () => {
    const [initializedState, setInitializedState] = useState(false)

    const [midiState, setMidiState] = useState({
        midiInputs,
        selectedInputId: useStorePads.getState().selectedMidiDeviceId || '',
        isSupported: midiSupported
    })

    useEffect(() => {
        const savedDevice = useStorePads.getState().selectedMidiDeviceId
        if (savedDevice) {
            selectedInputId = savedDevice
        }

        initMidi().then(() => setInitializedState(true))
    }, [])

    useEffect(() => {
        const unsubscribe = subscribeMidiState(setMidiState)
        return () => unsubscribe()
    }, [])

    return {
        initMidi,
        isMidiReady: initializedState && midiState.isSupported,
        isMidiSupported: midiState.isSupported,
        midiInputs: midiState.midiInputs,
        selectedInputId: midiState.selectedInputId,
        registerMidiTarget,
        unregisterMidiTarget,
        startMidiLearning,
        stopMidiLearning,
        getAssignedMidiAssignment,
        getNoteName,
        selectMidiDevice,
        applyMidiAssignmentToTarget,
        subscribeMidiMessages
    }
}

export default useMidiMapping
